import { useCallback, useState } from 'react'
import { usePopper } from 'react-popper'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { colors, breakpoints, structure, zIndex } from '@/styles'
import useMediaQuery from '@/hooks/use-media-query'
import { useTaskStates } from '@/models/toaster/hooks'
import { Label } from '@/components/text'
import Icon from '@/components/icon'
import ToasterItem from './toaster-item'
import { Header, Text, HeaderButton } from './elements'
import SubMenu from './submenu'
import { TaskKey, Tasks, TaskState } from './types'

const PositionWrapper = styled.div`
  position: fixed;
  bottom: 16px;
  z-index: ${zIndex.onboardingToaster + 1};

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    top: 0px;
    bottom: 0px;
    right: 0px;
  }
`

const Wrapper = styled.div`
  background-color: ${colors.white};
  border-radius: 8px;
  border: 1px solid ${colors.grey88};
  overflow: hidden;
  max-width: 304px;
  box-shadow:
    0px 10px 15px -3px rgba(0, 0, 0, 0.1),
    0px 4px 6px -2px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  max-height: calc(
    100vh - ${structure.header.height}px - var(--base-padding) - var(--base-padding)
  );

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    height: 100%;
    max-height: unset;
    max-width: 288px;
    border-radius: 0;
    border: none;
    border-left: 1px solid ${colors.grey88};
  }
`

const TitleBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const TitleBarButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const ItemsWrapper = styled.div`
  --base: 16px;
  --offset: 8px;
  overflow-y: auto;
  padding: 0 calc(var(--base) + var(--offset)) 0 var(--base);
  @supports (scrollbar-gutter: stable) {
    scrollbar-gutter: stable;
    padding: 0 max(var(--base) + var(--offset) - var(--scrollbar-width), 0px) 0 var(--base);
  }
`

const Items = styled.div`
  display: flex;
  flex-direction: column;
  padding: 8px 0;
  width: 100%;
`

const Popper = styled.div`
  z-index: 100;
`

type TosterProps = {
  data: Tasks
  setOpen: (x: boolean) => void
  setEnabled: (x: boolean) => void
  setHideCompleted: (x: boolean) => void
  hideCompleted?: boolean
  count: number
}

const stateOrder: Record<TaskState, number> = { error: 1, incomplete: 2, complete: 3 }
const getStateOrder = (state?: TaskState) => {
  if (!state) return stateOrder['incomplete']
  return stateOrder[state]
}

const Toaster: React.FC<TosterProps> = ({
  data,
  setOpen,
  setEnabled,
  hideCompleted,
  setHideCompleted,
  count
}) => {
  const { t } = useTranslation()
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)
  const toggleCompleted = () => setHideCompleted(!hideCompleted)
  const [visible, setVisible] = useState(false)
  const [openItem, setOpenItem] = useState<TaskKey | null>(null)
  const close = useCallback(() => setVisible(false), [setVisible])
  const [referenceElement, setReferenceElement] = useState<HTMLButtonElement | null>(null)
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null)
  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, 8]
        }
      }
    ]
  })
  const states = useTaskStates()
  const hasRemaining = count > 0
  return (
    <PositionWrapper
      onClick={e => {
        e.stopPropagation()
      }}
    >
      <Wrapper>
        <Header>
          <TitleBar>
            <Label type="large">{data.title}</Label>
            <TitleBarButtons>
              <HeaderButton
                title={t('b.toaster.view.settings.button', 'Settings')}
                aria-label={t('b.toaster.view.settings.button', 'Settings')}
                ref={setReferenceElement}
                onClick={() => {
                  setVisible(!visible)
                }}
              >
                <Icon id="ellipsis" width={18} height={4} />
              </HeaderButton>
              <HeaderButton
                title={t('b.toaster.view.minimize.button', 'Minimize')}
                aria-label={t('b.toaster.view.minimize.button', 'Minimize')}
                onClick={() => setOpen(false)}
              >
                {isMobile ? (
                  <Icon id="close_red" width={14} height={14} />
                ) : (
                  <Icon id="chevron" width={14} height={8} />
                )}
              </HeaderButton>
            </TitleBarButtons>
          </TitleBar>
          <Text>{data.description}</Text>
        </Header>
        {visible && (
          <Popper ref={setPopperElement} style={styles.popper} {...attributes.popper}>
            <SubMenu
              toggleCompleted={toggleCompleted}
              hideCompleted={hideCompleted}
              setEnabled={setEnabled}
              close={close}
            />
          </Popper>
        )}
        <ItemsWrapper>
          <Items>
            {data.tasks
              .sort((a, b) => getStateOrder(states[a.key]) - getStateOrder(states[b.key]))
              .map(({ key, ...rest }) => (
                <ToasterItem
                  key={`toast-item-${key}`}
                  id={key}
                  {...rest}
                  hideCompleted={hideCompleted && hasRemaining}
                  isOpen={openItem === key}
                  setOpen={setOpenItem}
                />
              ))}
          </Items>
        </ItemsWrapper>
      </Wrapper>
    </PositionWrapper>
  )
}

export default Toaster
